/**
 * Admin page for managing a single org-scoped template.
 *
 * Sections: Overview (edit metadata), Template Health (hub — activation
 * validation, schema audit, clause library, body tag inventory, all in one
 * card), Reference PDF, Versions (list + upload + rollback), Document
 * Properties & Mappings (combined table with dropdown), Schema.
 * Uses brand typography scale, .input/.label classes, and colour tokens.
 *
 * The Template Health hub (`TemplateHealthPanel`) absorbed what used to be
 * three separate surfaces on this page: the standalone Body Tags card, the
 * TagSummaryStrip rendering in the Versions card, and that card's inline
 * activation-warnings box. The Versions card keeps only what's specific to
 * version history: PreviewExtractionDetail, the previews-stale/rollback
 * banners, and VersionTagDiff per version row. Feeding the hub's clause
 * library section requires `useClauses(id)` here (the same clause-tag parse
 * ClauseManager runs) — `clausesLoaded` is only true once that query has
 * genuinely settled (not loading, not errored), so a fetch failure shows
 * "Checking clause library…" rather than a false all-clear.
 *
 * Clause preview staleness: `previewExtraction` is the provenance of the
 * last derived clause-preview refresh (runs automatically on every version
 * activation). A staleness banner renders when the status isn't 'complete',
 * OR when `sourceVersion` doesn't match `currentVersion` — the second arm
 * catches the case where the activation Lambda dies mid-refresh (timeout,
 * crash): no provenance is written at all, yet the version pointer has
 * already moved, so the mismatch is the only durable trace. Both promote
 * and rollback can also return an immediate `extractionWarning` on the
 * mutation result (separate from the durable banner, which only updates
 * once the query refetches) — the rollback handler captures that into local
 * state and renders it beside the banner.
 *
 * Two-phase upload: uploading a version now stages it for review rather than
 * activating it immediately (see TemplateUploadModal). Staged uploads still
 * awaiting review render as rows ABOVE the numbered version history here,
 * each with its own Download sample / Promote / Discard actions and a
 * staleness flag when the template's clause set has moved on since the
 * upload was staged. A row-triggered promote's `extractionWarning` can't be
 * shown ON the row after a successful promote — the staged record (and so
 * the row) is deleted as part of activation — so it's lifted to a card-level
 * banner (`promoteRowWarning`) instead, mirroring the existing rollback-
 * warning pattern. Errors stay on the row: a rejected promote/discard leaves
 * the staged record untouched, so the row that triggered it is still there
 * to show it.
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Upload, RotateCcw, Download } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useClauses } from "../../hooks";
import {
  useOrgTemplate,
  useUpdateOrgTemplate,
  useTemplateVersions,
  useStageTemplateVersion,
  usePromoteStagedVersion,
  useDiscardStagedVersion,
  useRollbackVersion,
  useUpdateFieldMappings,
  useUpdateReferencePdfMode,
} from "../../hooks/useOrgTemplates";
import { Card, Button, LoadingSpinner } from "../../components/common";
import { orgTemplatesApi, getTagValidationIssues } from "../../services/api";
import TemplateUploadModal from "../../components/admin/TemplateUploadModal";
import TemplateHealthPanel from "../../components/admin/TemplateHealthPanel";
import PreviewExtractionDetail from "../../components/admin/PreviewExtractionDetail";
import VersionTagDiff from "../../components/admin/VersionTagDiff";
import { diffBodyTags } from "../../utils/versionTagDiff";
import PropertyMappingTable from "../../components/admin/PropertyMappingTable";
import SchemaConfigurator from "../../components/admin/SchemaConfigurator";
import ReferencePdfUpload from "../../components/admin/ReferencePdfUpload";
import PerModulePdfManager from "../../components/admin/PerModulePdfManager";
import StagedVersionRow from "../../components/admin/StagedVersionRow";
import type { ReferencePdfMode } from "../../types/orgTemplate";

export default function TemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({
    name: "",
    description: "",
    category: "",
  });
  const [rollbackWarning, setRollbackWarning] = useState<string | null>(null);
  const [rollbackError, setRollbackError] = useState<string | null>(null);
  // Set only by a staged-row promote (see file header comment) — a
  // modal-triggered promote shows its own extractionWarning inside the modal
  // instead, so it never touches this state.
  const [promoteRowWarning, setPromoteRowWarning] = useState<string | null>(null);
  // Which staged row's action is in flight. The promote/discard mutation
  // instances are shared across every staged row (deliberate: all rows'
  // actions disable during any activation to avoid racing concurrent
  // promotes), so isPending alone can't say WHICH row to spin — this scopes
  // the loading state to the acted-on row.
  const [actingStagedId, setActingStagedId] = useState<string | null>(null);

  const { data: template, isLoading } = useOrgTemplate(id || "");
  const { data: versionsData } = useTemplateVersions(id || "");
  const {
    data: clausesData,
    isLoading: clausesLoading,
    isError: clausesIsError,
  } = useClauses(id || "");
  const updateTemplate = useUpdateOrgTemplate();
  const stageVersion = useStageTemplateVersion(id || "");
  const promoteStagedVersion = usePromoteStagedVersion(id || "");
  const discardStagedVersion = useDiscardStagedVersion(id || "");
  const rollbackVersion = useRollbackVersion(id || "");
  const updateFieldMappings = useUpdateFieldMappings(id || "");
  const updatePdfMode = useUpdateReferencePdfMode(id || "");

  if (user?.role !== "admin") {
    return (
      <div className="space-y-6">
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">
          Template Detail
        </h1>
        <Card>
          <p className="text-red-600">Access Denied. Admin role required.</p>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (!template) {
    return (
      <Card>
        <p className="text-slate-500">Template not found.</p>
      </Card>
    );
  }

  const versions = versionsData?.items || [];
  const staged = versionsData?.staged || [];

  // Same shape ClauseManager builds for parseClauseTags — only .id and
  // option .id are used, so this stays a thin projection of the clause list.
  const clauseRefs = clausesData?.items.map((c) => ({
    id: c.id,
    options: c.options.map((o) => ({ id: o.id })),
  }));
  // Honest loading gate: only true once the query has genuinely settled, so
  // a fetch failure keeps the hub showing "Checking clause library…"
  // instead of a false all-clear.
  const clausesLoaded = !clausesLoading && !clausesIsError;

  const pe = template.previewExtraction;
  const previewsStale = pe
    ? pe.status !== "complete" || pe.sourceVersion !== template.currentVersion
    : template.currentVersion > 1; // versions exist but extraction never recorded

  const handleStage = (file: File, changeNotes?: string) =>
    stageVersion.mutateAsync({ file, changeNotes });

  // Used by BOTH the modal's Promote button (which shows its own
  // extractionWarning inline) and staged-row Promote buttons — the row
  // variant additionally lifts extractionWarning to promoteRowWarning since
  // the row itself is gone by the time a caller could render on it.
  const handleModalPromote = async (stagedId: string) => {
    const result = await promoteStagedVersion.mutateAsync(stagedId);
    // A successful activation supersedes any earlier rollback warning.
    setRollbackWarning(null);
    return result;
  };

  const handleRowPromote = async (stagedId: string) => {
    setActingStagedId(stagedId);
    try {
      const result = await promoteStagedVersion.mutateAsync(stagedId);
      // A successful activation supersedes any earlier rollback warning.
      setRollbackWarning(null);
      setPromoteRowWarning(
        result.extractionWarning ? `Version ${result.version} activated. ${result.extractionWarning}` : null
      );
      return result;
    } finally {
      setActingStagedId(null);
    }
  };

  const handleDiscard = async (stagedId: string) => {
    setActingStagedId(stagedId);
    try {
      await discardStagedVersion.mutateAsync(stagedId);
    } finally {
      setActingStagedId(null);
    }
  };

  const handleDownloadSample = async (stagedId: string) => {
    const { url } = await orgTemplatesApi.getStagedSampleUrl(template.id, stagedId);
    window.open(url, "_blank");
  };

  const handleStartEdit = () => {
    setMetaForm({
      name: template.name,
      description: template.description || "",
      category: template.category,
    });
    setIsEditingMeta(true);
  };

  const handleSaveMeta = async () => {
    await updateTemplate.mutateAsync({ id: template.id, input: metaForm });
    setIsEditingMeta(false);
  };

  const handleRollback = async (version: number) => {
    setRollbackError(null);
    try {
      const result = await rollbackVersion.mutateAsync(version);
      setRollbackWarning(result.extractionWarning ?? null);
    } catch (err) {
      // Rollback is gated exactly like upload: a version valid when uploaded can
      // be invalidated by later clause edits, so it can be rejected the same way.
      const issues = getTagValidationIssues(err);
      setRollbackWarning(null);
      setRollbackError(
        issues
          ? `This version's clause markup no longer matches the clause set, so it was not activated: ${issues
              .map((issue) => `${issue.tag ? `{${issue.tag}} ` : ''}${issue.message}`)
              .join('; ')}`
          : err instanceof Error
            ? err.message
            : 'Rollback failed. Please try again.',
      );
    }
  };

  const handleDownload = async (version: number) => {
    const { url } = await orgTemplatesApi.getVersionDownloadUrl(
      template.id,
      version,
    );
    window.open(url, "_blank");
  };

  const handleSaveFieldMappings = async (
    mappings: typeof template.fieldMappings,
  ) => {
    await updateFieldMappings.mutateAsync({ mappings });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          iconOnly
          size="md"
          icon={<ArrowLeft className="h-5 w-5" />}
          aria-label="Back to templates"
          onClick={() => navigate("/generate/templates")}
        />
        <div className="flex-1">
          <h1 className="text-h1 text-slate-900 dark:text-slate-50">
            {template.name}
          </h1>
          {template.description && (
            <p className="text-body-sm text-slate-500 dark:text-slate-400 mt-1">
              {template.description}
            </p>
          )}
        </div>
        <Button onClick={() => setIsUploadOpen(true)}>
          <Upload className="h-4 w-4 mr-2" />
          Upload Version
        </Button>
      </div>

      {/* Overview Section */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h3 text-slate-900 dark:text-slate-100">
            Overview
          </h2>
          {!isEditingMeta && (
            <Button variant="secondary" size="sm" onClick={handleStartEdit}>
              Edit
            </Button>
          )}
        </div>

        {isEditingMeta ? (
          <div className="space-y-4">
            <div>
              <label className="label">Name</label>
              <input
                type="text"
                value={metaForm.name}
                onChange={(e) =>
                  setMetaForm({ ...metaForm, name: e.target.value })
                }
                className="input mt-1"
              />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea
                value={metaForm.description}
                onChange={(e) =>
                  setMetaForm({ ...metaForm, description: e.target.value })
                }
                rows={2}
                className="input mt-1"
              />
            </div>
            <div>
              <label className="label">Category</label>
              <input
                type="text"
                value={metaForm.category}
                onChange={(e) =>
                  setMetaForm({ ...metaForm, category: e.target.value })
                }
                className="input mt-1"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setIsEditingMeta(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMeta}
                loading={updateTemplate.isPending}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-2 gap-4 text-body-sm">
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Category</dt>
              <dd className="text-slate-900 dark:text-slate-100 capitalize">
                {template.category}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">
                Current Version
              </dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {template.currentVersion > 0
                  ? `v${template.currentVersion}`
                  : "No uploads"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Status</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {template.isActive ? "Active" : "Inactive"}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 dark:text-slate-400">Created</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {new Date(template.createdAt).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        )}
      </Card>

      {/* Template Health hub — activation validation, schema audit, clause
          library, body tag inventory, all in one place */}
      <TemplateHealthPanel
        bodyTags={template.bodyTags}
        schema={template.schema}
        tagValidation={template.tagValidation}
        clauseRefs={clauseRefs}
        clausesLoaded={clausesLoaded}
        onAddClauseFromTag={(tag) =>
          navigate(`/generate/clauses?template=${id}&sourceTag=${encodeURIComponent(tag)}`)
        }
      />

      {/* Reference PDF Section */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-100 mb-4">
          Reference PDF
        </h2>
        <p className="text-body-sm text-slate-500 dark:text-slate-400 mb-4">
          Upload a reference PDF for clause navigation in the contract wizard.
          Users can view the original document while configuring clauses.
        </p>

        {/* PDF Mode Selector */}
        <div className="mb-6">
          <label htmlFor="pdfMode" className="label">
            PDF Mode
          </label>
          <select
            id="pdfMode"
            value={template.referencePdfMode || "single"}
            onChange={(e) => {
              const newMode = e.target.value as ReferencePdfMode;
              const currentMode = template.referencePdfMode || "single";
              if (newMode !== currentMode) {
                const switchingToSingle =
                  newMode === "single" && currentMode === "per-module";
                const hasPerModulePdfs =
                  template.referencePdfKeys?.contractDetails ||
                  Object.keys(template.referencePdfKeys?.modules || {}).length >
                    0;
                let cleanupOldFiles = false;

                if (switchingToSingle && hasPerModulePdfs) {
                  cleanupOldFiles = confirm(
                    "Switching to single PDF mode. Do you want to delete the existing per-module PDFs?",
                  );
                }

                updatePdfMode.mutate({ mode: newMode, cleanupOldFiles });
              }
            }}
            className="input"
            disabled={updatePdfMode.isPending}
          >
            <option value="single">Single PDF for all clauses</option>
            <option value="per-module">Separate PDF per module</option>
          </select>
          <p className="text-caption text-slate-500 dark:text-slate-400 mt-1">
            {(template.referencePdfMode || "single") === "per-module"
              ? "Upload a different PDF for Contract Details and each module."
              : "One PDF is used for all clauses in the wizard."}
          </p>
        </div>

        {/* Show mode-specific upload UI */}
        {(template.referencePdfMode || "single") === "per-module" ? (
          <PerModulePdfManager template={template} />
        ) : (
          <ReferencePdfUpload
            templateId={template.id}
            currentPdfKey={template.referencePdfKey}
          />
        )}
      </Card>

      {/* Versions Section */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-100 mb-4">
          Versions
        </h2>

        {pe && (
          <div className="mb-4">
            <PreviewExtractionDetail extraction={pe} />
          </div>
        )}

        {previewsStale && (
          <div
            role="status"
            className="mb-4 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-body-sm text-amber-800 dark:text-amber-200"
          >
            Clause previews may not match the current document
            {pe
              ? ` (last extraction: ${pe.status}, document version ${pe.sourceVersion} of ${template.currentVersion})`
              : ""}
            . Previews refresh automatically on the next version upload; see the
            template runbook for the reconcile script.
          </div>
        )}

        {rollbackWarning && (
          <div
            role="status"
            className="mb-4 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-body-sm text-amber-800 dark:text-amber-200"
          >
            {rollbackWarning}
          </div>
        )}

        {rollbackError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-body-sm text-red-800 dark:text-red-200"
          >
            {rollbackError}
          </div>
        )}

        {promoteRowWarning && (
          <div
            role="status"
            className="mb-4 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-body-sm text-amber-800 dark:text-amber-200"
          >
            {promoteRowWarning}
          </div>
        )}

        {staged.length > 0 && (
          <div className="space-y-3 mb-4">
            {staged.map((s) => (
              <StagedVersionRow
                key={s.stagedId}
                staged={s}
                isStale={(template.clauseTagRevision ?? 0) !== s.clauseTagRevision}
                onDownloadSample={handleDownloadSample}
                onPromote={handleRowPromote}
                onDiscard={handleDiscard}
                isPromoting={promoteStagedVersion.isPending && actingStagedId === s.stagedId}
                isDiscarding={discardStagedVersion.isPending && actingStagedId === s.stagedId}
                actionsDisabled={promoteStagedVersion.isPending || discardStagedVersion.isPending}
              />
            ))}
          </div>
        )}

        {versions.length === 0 ? (
          <p className="text-body-sm text-slate-500 dark:text-slate-400">
            No versions uploaded yet.
          </p>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => (
              <div
                key={version.version}
                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-md"
              >
                <div>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    v{version.version}
                  </span>
                  {version.changeNotes && (
                    <span className="ml-3 text-body-sm text-slate-500 dark:text-slate-400">
                      {version.changeNotes}
                    </span>
                  )}
                  <span className="ml-3 text-caption text-slate-400">
                    {new Date(version.uploadedAt).toLocaleDateString()}
                  </span>
                  <span className="ml-2 text-caption text-slate-400 dark:text-slate-500">
                    by {version.uploadedBy}
                  </span>
                  {version.version === template.currentVersion && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-caption font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
                      Current
                    </span>
                  )}
                  {(() => {
                    // Diff against the previous version PRESENT in the list —
                    // with pagination the true n-1 may be absent, and a diff
                    // against a non-adjacent version would mislead.
                    const predecessor = versions.find(
                      (v) => v.version === version.version - 1
                    );
                    if (!predecessor) return null;
                    const diff = diffBodyTags(predecessor.bodyTags, version.bodyTags);
                    return <VersionTagDiff added={diff.added} removed={diff.removed} />;
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    iconOnly
                    size="sm"
                    icon={<Download className="h-4 w-4" />}
                    aria-label={`Download v${version.version}`}
                    onClick={() => handleDownload(version.version)}
                    className="btn-icon-compact"
                  />
                  {version.version !== template.currentVersion && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRollback(version.version)}
                      loading={rollbackVersion.isPending}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Rollback
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Document Properties & Mappings Section */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-100 mb-4">
          Document Properties & Mappings
        </h2>
        <PropertyMappingTable
          documentProperties={template.documentProperties}
          fieldMappings={template.fieldMappings}
          onSave={handleSaveFieldMappings}
          isSaving={updateFieldMappings.isPending}
        />
      </Card>

      {/* Schema Section */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-100 mb-4">
          Schema
        </h2>
        <SchemaConfigurator
          templateId={template.id}
          schema={template.schema}
          bodyTags={template.bodyTags}
        />
      </Card>

      {/* Upload Modal */}
      <TemplateUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUpload={handleStage}
        onPromote={handleModalPromote}
        onDiscard={handleDiscard}
        isUploading={stageVersion.isPending}
        isPromoting={promoteStagedVersion.isPending}
        isDiscarding={discardStagedVersion.isPending}
      />
    </div>
  );
}
